import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ToastModule } from 'primeng/toast';
import { DropdownModule } from 'primeng/dropdown';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../services/auth.service';
import { Router, ActivatedRoute } from '@angular/router';
import { PasscodeDialogComponent } from '../../components/passcode-dialog/passcode-dialog.component';
import { WorkspaceDto } from '../../dto/workspace.dto';
import { TenantInfo } from '../../dto/auth.dto';

@Component({
  selector: 'app-auth',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    InputTextModule,
    PasswordModule,
    ToastModule,
    DropdownModule,
    PasscodeDialogComponent,
  ],
  providers: [MessageService],
  template: `
    <div
      class="surface-ground h-screen flex align-items-center justify-content-center"
    >
      <!-- Auth Form (hidden when tenant selection is shown) -->
      <div
        *ngIf="!showTenantSelection"
        class="surface-card p-4 shadow-2 border-round w-full lg:w-4"
      >
        <div class="text-center mb-5">
          <h2>{{ isSignup ? 'Create Account' : 'Sign In' }}</h2>
        </div>

        <!-- Workspace Selection (only shown when allowAll=true) -->
        <div *ngIf="showWorkspaceSelection" class="mb-4">
          <label for="workspace-select" class="block text-900 font-medium mb-2"
            >Select Workspace</label
          >
          <p-dropdown
            id="workspace-select"
            [options]="workspaces"
            [(ngModel)]="workspaceId"
            optionLabel="name"
            optionValue="id"
            placeholder="Choose a workspace"
            (onChange)="onWorkspaceChange()"
            [loading]="loadingWorkspaces"
            styleClass="w-full"
          >
            <ng-template pTemplate="item" let-workspace>
              <div class="flex align-items-center">
                <div>
                  <div class="font-medium">{{ workspace.name }}</div>
                  <div class="text-sm text-600">{{ workspace.code }}</div>
                </div>
              </div>
            </ng-template>
          </p-dropdown>
        </div>

        <div>
          <!-- Login Form -->

          <p-button
            *ngIf="hasMicrosoftIntegration"
            label="Sign In with Microsoft"
            (onClick)="loginWithMicrosoft()"
            [loading]="loading"
            styleClass="w-full mb-3"
          ></p-button>

          <p-button
            *ngIf="hasGoogleIntegration"
            label="Sign In with Google"
            (onClick)="loginWithGoogle()"
            [loading]="loading"
            styleClass="w-full mb-3"
          ></p-button>

          <p-button
            *ngIf="hasPasscodeIntegration"
            label="PassCode"
            (onClick)="showPasscodeDialog()"
            [loading]="loading"
            styleClass="w-full mb-3"
          ></p-button>
        </div>
      </div>
    </div>

    <!-- Features Link -->
    <div class="text-center mt-4">
      <p class="text-600 mb-3">Want to learn more about our features?</p>
      <p-button
        label="View Features"
        icon="pi pi-star"
        (onClick)="goToFeatures()"
        severity="secondary"
        styleClass="p-button-outlined"
      ></p-button>
    </div>

    <!-- Passcode Dialog -->
    <app-passcode-dialog
      (passcodeSubmitted)="onPasscodeSubmitted($event)"
      (cancelled)="onPasscodeCancelled()"
    ></app-passcode-dialog>

    <!-- Tenant Selection -->
    <div
      *ngIf="showTenantSelection"
      class="surface-card p-4 shadow-2 border-round w-full lg:w-4"
      style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 1000;"
    >
      <div class="text-center mb-5">
        <h2>Select Tenant</h2>
        <p class="text-600">Choose which tenant you want to access</p>
      </div>

      <!-- Tenant Information -->
      <div class="mb-4 p-3 border-round bg-blue-50 border-1 border-blue-200">
        <div class="flex align-items-start gap-3">
          <i class="pi pi-info-circle text-blue-600 mt-1"></i>
          <div>
            <h4 class="text-blue-900 mb-2">What is a Tenant?</h4>
            <p class="text-blue-800 mb-2">
              A tenant is a separate organization or department within your
              workspace. Each tenant has its own data, users, and settings,
              allowing you to manage different groups independently.
            </p>
            <p class="text-blue-800 text-sm">
              <strong>Note:</strong> You can switch between tenants later by
              going to the Tenant page in the main navigation menu. Click on
              your profile picture in the top right corner and select "Tenant".
            </p>
          </div>
        </div>
      </div>

      <div class="flex flex-column gap-3">
        <div
          *ngFor="let tenant of availableTenants"
          class="p-3 border-round cursor-pointer transition-colors transition-duration-150"
          [class.bg-primary-50]="selectedTenant?.id === tenant.id"
          [class.border-primary]="selectedTenant?.id === tenant.id"
          [class.border-1]="selectedTenant?.id === tenant.id"
          (click)="selectedTenant = tenant"
        >
          <div class="flex align-items-center justify-content-between">
            <div>
              <div class="font-medium text-900">{{ tenant.name }}</div>
            </div>
            <i
              *ngIf="selectedTenant?.id === tenant.id"
              class="pi pi-check text-primary"
            ></i>
          </div>
        </div>
      </div>

      <div class="flex justify-content-center gap-2 mt-4">
        <p-button
          label="Cancel"
          (onClick)="onTenantSelectionCancelled()"
          severity="secondary"
        ></p-button>
        <p-button
          label="Continue"
          (onClick)="onTenantSelected()"
          [disabled]="!selectedTenant"
        ></p-button>
      </div>
    </div>
  `,
  styles: [],
})
export class AuthComponent implements OnInit {
  @ViewChild(PasscodeDialogComponent) passcodeDialog!: PasscodeDialogComponent;

  isSignup = false;
  loading = false;
  hasPasscodeIntegration = false;
  hasMicrosoftIntegration = false;
  hasGoogleIntegration = false;
  showWorkspaceSelection = false;
  loadingWorkspaces = false;
  workspaceId: string = '';
  workspaces: WorkspaceDto[] = [];

  // Tenant selection state
  showTenantSelection = false;
  availableTenants: TenantInfo[] = [];
  selectedTenant: TenantInfo | null = null;

  credentials: {
    username?: string;
    password?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
  } = {};

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    // Check for allowAll query parameter
    this.route.queryParams.subscribe((params) => {
      if (params['allowAll'] === 'true') {
        this.showWorkspaceSelection = true;
        this.loadAllWorkspaces();
      } else {
        // Load auth setup configuration from backend for current workspace
        this.loadAuthSetup();
      }
    });
  }

  private loadAuthSetup() {
    this.authService.loadAuthSetup().subscribe({
      next: (setup) => {
        this.hasPasscodeIntegration = !!setup.data.passcodeIntegration;
        this.hasMicrosoftIntegration = !!setup.data.microsoftIntegration;
        this.hasGoogleIntegration = !!setup.data.googleIntegration;
      },
      error: (error) => {
        console.warn('Failed to load auth setup:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load authentication configuration',
        });
      },
    });
  }

  private loadAllWorkspaces() {
    this.loadingWorkspaces = true;
    // Get all workspaces from auth endpoint
    this.authService.getAllWorkspaces().subscribe({
      next: (workspaces) => {
        this.workspaces = workspaces;
        this.loadingWorkspaces = false;
        if (workspaces.length === 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'No Workspaces',
            detail: 'No workspaces found',
          });
        }
      },
      error: (error) => {
        console.warn('Failed to load workspaces:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load workspaces',
        });
        this.loadingWorkspaces = false;
      },
    });
  }

  onWorkspaceChange() {
    // Clear the auth setup cache to ensure fresh data for the new workspace
    this.authService.clearAuthSetupCache();
    // Load auth setup for the selected workspace
    this.loadAuthSetupForWorkspace(this.workspaceId);
  }

  private loadAuthSetupForWorkspace(workspaceId: string) {
    this.loading = true;
    this.authService.loadAuthSetupForWorkspace(workspaceId).subscribe({
      next: (setup) => {
        this.hasPasscodeIntegration = !!setup.data.passcodeIntegration;
        this.hasMicrosoftIntegration = !!setup.data.microsoftIntegration;
        this.hasGoogleIntegration = !!setup.data.googleIntegration;
        this.loading = false;
        this.workspaceId = setup?.data?.workspace?.id || '';
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Workspace configuration loaded',
        });
      },
      error: (error) => {
        console.warn('Failed to load auth setup for workspace:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail:
            'Failed to load authentication configuration for selected workspace',
        });
        this.loading = false;
      },
    });
  }

  signin() {
    if (!this.credentials.username || !this.credentials.password) return;

    this.loading = true;
    this.authService
      .signin(this.credentials.username, this.credentials.password)
      .subscribe({
        next: (response) => {
          this.handleAuthResponse(response);
          this.loading = false;
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error.message,
          });
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  signup() {
    if (!this.validateSignup()) {
      return;
    }

    this.loading = true;
    this.authService
      .signup(
        this.credentials as {
          username: string;
          password: string;
          email: string;
          firstName: string;
          lastName: string;
          phone?: string;
          tenantName: string;
        }
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Account created successfully. Please sign in.',
          });
          this.isSignup = false;
          this.loading = false;
          this.credentials = {};
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error.error?.message || 'Failed to create account',
          });
          this.loading = false;
        },
      });
  }

  private validateSignup(): boolean {
    if (
      !this.credentials.username ||
      !this.credentials.password ||
      !this.credentials.email ||
      !this.credentials.firstName ||
      !this.credentials.lastName
    ) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill in all required fields',
      });
      return false;
    }
    return true;
  }

  async loginWithMicrosoft() {
    await this.authService.redirectToMicrosoftLogin();
  }

  async loginWithGoogle() {
    await this.authService.redirectToGoogleLogin();
  }

  loginWithCognito() {
    this.authService.redirectToCognitoLogin();
  }

  showPasscodeDialog() {
    this.passcodeDialog.show();
  }

  onPasscodeSubmitted(passcode: string) {
    this.loading = true;
    // Pass the selected workspace ID if available
    const workspaceId = this.workspaceId || '';
    this.authService.signinWithPasscode(passcode, workspaceId).subscribe({
      next: (response) => {
        this.handleAuthResponse(response);
        this.loading = false;
        // Hide the passcode dialog after processing the response
        this.passcodeDialog.hide();
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Invalid passcode',
        });
        this.loading = false;
        // Show error in dialog
        this.passcodeDialog.setError();
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  onPasscodeCancelled() {
    // Dialog is already hidden by the component
  }

  /**
   * Handle authentication response and check for tenant selection
   */
  private handleAuthResponse(response: any) {
    console.log('Auth response received:', response);
    console.log('User tenants:', response.user?.tenants);
    console.log('Current tenant ID:', response.currentTenantId);
    console.log('Tenants length:', response.user?.tenants?.length);

    // Check if user has multiple tenants available
    if (response.user?.tenants && response.user.tenants.length > 1) {
      console.log('Multiple tenants found, showing tenant selection');
      console.log('Available tenants:', response.user.tenants);
      this.availableTenants = response.user.tenants;
      this.showTenantSelection = true;
      console.log('showTenantSelection set to:', this.showTenantSelection);
      console.log('availableTenants set to:', this.availableTenants);
      this.messageService.add({
        severity: 'info',
        summary: 'Select Tenant',
        detail: 'Please select a tenant to continue',
      });
    } else if (response.user?.tenants && response.user.tenants.length === 1) {
      console.log('Single tenant found, auto-selecting');
      // Auto-select the single tenant
      const singleTenant = response.user.tenants[0];
      this.authService.selectTenant(singleTenant);
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: `Logged in to ${singleTenant.name}`,
      });
      this.router.navigate(['/home']);
    } else {
      console.log('No tenants found, proceeding to home');
      // No tenant selection needed, proceed to home
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Login successful',
      });
      this.router.navigate(['/home']);
    }
  }

  /**
   * Handle tenant selection
   */
  onTenantSelected() {
    if (this.selectedTenant) {
      // Set the selected tenant in the auth service
      this.authService.selectTenant(this.selectedTenant);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: `Logged in to ${this.selectedTenant.name}`,
      });

      this.router.navigate(['/home']);
    }
  }

  /**
   * Cancel tenant selection and logout
   */
  onTenantSelectionCancelled() {
    this.authService.logout();
    this.showTenantSelection = false;
    this.availableTenants = [];
    this.selectedTenant = null;
  }

  /**
   * Navigate to features page
   */
  goToFeatures() {
    this.router.navigate(['/features']);
  }
}
